import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStep, MatVerticalStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { AppCluster, AppKeyService, IAppKey } from '@services/appkey/';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ProgressIndicatorService } from '@services/progress';
import { I18nService } from '@src/app/i18n/services';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DialogService } from '../../../../../../services/dialog';
import { IAppRequiredTerms, LegalsService } from '../../../../../../services/legals';
import { FormEditorInputType } from '../../../../../../shared/components';
import { NewAppKey } from './appkey-new.models';

@Component({
	selector: 'app-appkey-new',
	templateUrl: './appkey-new.dialog.html',
	styleUrls: ['./appkey-new.dialog.scss']
})
export class AppKeyNewDialogComponent implements OnInit, AfterViewInit {
	@ViewChild(MatVerticalStepper) stepper: MatVerticalStepper;

	private dialogRefClose: () => void;
	public appKey: IAppKey;
	public cluster: AppCluster;
	public clusterId: string;
	public clusters: AppCluster[] = [];
	public provider: string;
	public providers: string[] = ["Cognito", "Entra"];
	public appKeyLegals: IAppRequiredTerms;

	public formEditorInputType = FormEditorInputType;
	public selectedStepIndex = 0;
	public unsavedChanges = {
		0: false,
		1: false
	};

	public createForm = this.formBuilder.group({
		name: [{ value: '', disabled: true }, [Validators.required, this.formsService.appNameValidator()]],
		clientId: [{ value: '', disabled: true }, [Validators.required, this.formsService.uuidValidator()]]
	});

	constructor(
		private appkeyService: AppKeyService,
		public i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private formsService: FormsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: NewAppKey,
		private dialogRef: MatDialogRef<AppKeyNewDialogComponent>,
		private router: Router,
		private formBuilder: FormBuilder,
		private legalsService: LegalsService,
		private dialogService: DialogService
	) {
		// Store original close function and replace with unsaved version, remember to restore on use
		this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog);
	}

	public ngOnInit(): void {
		this.getClusters();
	}

	public ngAfterViewInit(): void {
		this.stepper.selectionChange.subscribe((res) => {
			this.selectedStepIndex = res.selectedIndex;

			// Any steps needing more space to be included here
			if (this.selectedStepIndex === 2) {
				this.dialogRef.removePanelClass('dialog-medium');
				this.dialogRef.addPanelClass('dialog-large');
			} else {
				this.dialogRef.removePanelClass('dialog-large');
				this.dialogRef.addPanelClass('dialog-medium');
			}
		});

		this.createForm.valueChanges.subscribe(() => (this.unsavedChanges['0'] = this.clusterId && this.createForm.valid));

		this.stepper.steps.forEach((step: MatStep, index: number) => {
			step.select = () => {
				if (
					(this.stepper.steps.get(this.selectedStepIndex).optional || this.stepper.steps.get(this.selectedStepIndex).completed) &&
					step.editable &&
					this.selectedStepIndex !== index
				)
					this.selectStep(index);
			};
		});
	}

	public resetStep(stepIndex: number): void {
		switch (stepIndex) {
			case 0:
				this.stepper.steps.get(stepIndex).reset();
				break;
			case 1:
				const appKey = { ...this.appKey };
				this.appKey = null;

				// This will ensure template updates correctly and form is being recreated
				setTimeout(() => {
					this.appKey = appKey;
				});
				break;
			default:
				this.stepper.selected.reset();
		}
	}

	public selectStep(stepIndex: number): void {
		if (this.stepper.steps.get(stepIndex)) {
			this.pendingChanges(this.unsavedChanges[this.selectedStepIndex], () => (this.selectedStepIndex = stepIndex));
		} else {
			this.closeDialog();
		}
	}

	public nextStep(): void {
		setTimeout(() => this.stepper.next());
	}

	public pendingChanges(unsavedChanges: boolean, callback: () => void): void {
		if (unsavedChanges) {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nService.get(
					'You have unsaved changes that will be lost. Are you sure you want to continue?',
					'app.prompt.pending-changes-confirm-message'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) callback();
				}
			});
		} else {
			callback();
		}
	}

	public closeDialog(): void {
		this.pendingChanges(this.unsavedChanges[this.selectedStepIndex], () => {
			this.dialogRefClose();
			if (this.dialogData?.callback) this.dialogData.callback(null);
		});
	}

	public viewDocumentation(): void {
		this.pendingChanges(this.unsavedChanges[this.selectedStepIndex], () => {
			this.dialogRefClose();
			this.router.navigate(['/api-documentation', this.clusterId ? this.clusterId : '']);
		});
	}

	public providerSelected(provider: string): void {
		this.provider = provider;
		this.createForm.controls.name.enable();
		if (provider == "Entra") {
			this.createForm.controls.clientId.enable();
		} else {
			this.createForm.controls.clientId.disable();
		}
	}

	public cancel(): void {
		if (this.dialogData.callback) {
			this.dialogData.callback(null);
		}
	}

	public createAppKey(): void {
		if (this.createForm.invalid) return;

		this.progressIndicatorService.spinner(true);

		this.appkeyService
			.create({
				...this.dialogData.appkey,
				name: this.createForm.value.name,
				clusterId: this.clusterId,
				clientId: this.createForm.value.clientId,
				provider: this.provider?.toLowerCase()
			})
			.subscribe(
				(appKey: IAppKey) => {
					this.appKey = appKey;
					this.cluster = this.clusters.find((cluster) => cluster.clusterId === this.clusterId);

					this.feedbackService.notification({
						msg: this.i18nService.get('The app key has been created.', 'app.descriptor.appkey.created'),
						config: { duration: 4000 }
					});

					this.getAppKeyLegals();

					if (this.dialogData.callback) this.dialogData.callback(appKey);
				},
				(error: any) => {
					this.feedbackService.apiError(error);
					this.progressIndicatorService.spinner(false);
				}
			);
	}

	private getClusters(): void {
		this.progressIndicatorService.spinner(true);

		this.appkeyService
			.getClusters()
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(clusters: AppCluster[]) => {
					this.clusters = clusters;
					this.cluster = clusters[0];
					this.clusterId = this.cluster.clusterId;
				},
				(error: any) => this.feedbackService.apiError(error)
			);
	}

	private getAppKeyLegals(): void {
		this.legalsService.getClientIdTerms(this.appKey.clientId)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(terms: IAppRequiredTerms) => {
					this.appKeyLegals = terms;
					this.nextStep();
				},
				(error: any) => {
					this.feedbackService.apiError(error);
				}
			);
	}
}
